import Logger from "@insite/client-framework/Logger";
import { PARAM_PREFIX } from "@tSurya/Handlers/Configurator/ConfiguratorUrlUtils";
import { MGBW_CYLINDO_FEATURES, MGBW_CYLINDO_UNSUPPORTED, MGBW_CYLINDO_UNSUPPORTED_INPUT, MGBW_SELECTIONS, MGBW_SELECTIONS_INPUT } from "@tSurya/Common/MgbwPropertyKeys";
import { PreConfiguredStylePlpModel } from "@tSurya/Store/Data/ConfiguratorModels";

/** Shape of each entry in the mgbw_selections JSON array on cart line properties. */
export interface MgbwSelectionEntry {
    OptionId: string;
    ValueId: string;
    Type: string;
    Name: string;
    Value: string;
    Visible: boolean;
}

/** Common shape for line property dictionaries (cart lines, wish list lines, etc.). */
export type MgbwLineProperties = { readonly [key: string]: string | undefined } | undefined;

/**
 * Converts an array of MGBW selection entries to a dictionary format for selection input.
 * @param selections The array of MGBW selection entries.
 * @returns A record mapping option IDs to value IDs.
 */
export function convertSelectionsToInput(selections: MgbwSelectionEntry[]): Record<string, string> {
    if (!selections) {
        return {};
    }
    return selections.reduce((acc, selection) => {
        acc[selection.OptionId] = selection.ValueId;
        return acc;
    }, {} as Record<string, string>);
}

/**
 * Parses a raw mgbw_selections JSON string into an array of selection entries.
 * Returns an empty array when the value is missing or cannot be parsed.
 * @param raw The raw mgbw_selections JSON string.
 */
export function parseMgbwSelections(raw: string | undefined): MgbwSelectionEntry[] {
    if (!raw) {
        return [];
    }
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as MgbwSelectionEntry[]) : [];
    } catch {
        return [];
    }
}

/**
 * Reads and parses all MGBW configurator selections from a line's properties.
 * @param properties The line properties dictionary.
 */
export function getMgbwSelections(properties: MgbwLineProperties): MgbwSelectionEntry[] {
    return parseMgbwSelections(properties?.[MGBW_SELECTIONS]);
}

/**
 * Reads the MGBW configurator selections from a line's properties and returns only the
 * entries flagged as visible (the ones shown to shoppers).
 * @param properties The line properties dictionary.
 */
export function getVisibleMgbwSelections(properties: MgbwLineProperties): MgbwSelectionEntry[] {
    return getMgbwSelections(properties).filter(entry => entry.Visible);
}

/**
 * Reads the MGBW configurator selections from a line's properties and converts them into
 * the optionId -> valueId record used for selection input.
 * @param properties The line properties dictionary.
 */
export function getMgbwSelectionsRecord(properties: MgbwLineProperties): Record<string, string> {
    return convertSelectionsToInput(getMgbwSelections(properties));
}

/**
 * Attempts to parse selections JSON and convert it into serialized
 * mgbw_selectionsInput payload format.
 * @param selectionsJson The raw mgbw_selections JSON string.
 * @returns Serialized selections input payload or undefined when parsing fails.
 */
export function tryParseSelections(selectionsJson?: string): string | undefined {
    if (!selectionsJson) {
        return undefined;
    }

    try {
        const selections = JSON.parse(selectionsJson) as MgbwSelectionEntry[];
        return JSON.stringify(convertSelectionsToInput(selections));
    } catch (e) {
        Logger.warn("tryParseSelections: Failed to parse selections JSON", { selectionsJson, error: e });
        return undefined;
    }
}

/**
 * Translates the persisted configurator properties of an existing line (cart, wish list, order)
 * into the transient input properties the add/update handlers expect. Keeping the Cylindo feature
 * map and the unsupported-feature flag together with the selections is what makes a copied line
 * resolve to the exact same preview image as its source.
 * @param properties The source line's properties dictionary.
 */
export function buildConfiguratorLineInput(properties: MgbwLineProperties): Record<string, string> {
    const input: Record<string, string> = {};

    const selectionsInput = tryParseSelections(properties?.[MGBW_SELECTIONS]);
    if (selectionsInput) {
        input[MGBW_SELECTIONS_INPUT] = selectionsInput;
    }

    const cylindoFeatures = properties?.[MGBW_CYLINDO_FEATURES];
    if (cylindoFeatures) {
        input[MGBW_CYLINDO_FEATURES] = cylindoFeatures;
    }

    const cylindoUnsupported = properties?.[MGBW_CYLINDO_UNSUPPORTED];
    if (cylindoUnsupported) {
        input[MGBW_CYLINDO_UNSUPPORTED_INPUT] = cylindoUnsupported;
    }

    return input;
}


export const parseMgbwStyles = (rawStyles: string | undefined): PreConfiguredStylePlpModel[] => {
    if (!rawStyles) {
        return [];
    }

    try {
        const parsed = JSON.parse(rawStyles) as PreConfiguredStylePlpModel[];
        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.filter(style => !!style);
    } catch {
        return [];
    }
};
